import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

import type { CursorHistory } from "@/components/table/Pagination";

/**
 * Which result set the list on screen is paging through.
 *
 * `SectionPage` provides it and changes it whenever the section, the filter,
 * the search or the sort changes — the four things that decide which rows
 * there are, and so which position a cursor means. Outside a section there is
 * none, and `useCursorHistory` is plain state.
 */
export const ResultSetContext = createContext<string | null>(null);

/**
 * A new array each time: the trail is state, and one shared array would be one
 * a screen could mutate for every list.
 */
const firstPage = (): CursorHistory => [undefined];

/**
 * A paged list's cursor trail, which returns to the first page by itself when
 * the rows it is paging through change (#458).
 *
 * A cursor is a position in one result set. Under another filter, search or
 * sort it points somewhere else: with the API's offset cursors, Next twice
 * (`o50`) and then a filter that leaves 28 rows lands past the end, and the
 * list says "Nothing matches" over rows that are there. Only the page size
 * reset the trail, and `SectionPage` renders one screen element throughout, so
 * nothing remounted it either.
 *
 * Every paged list holds its trail with this, not `useState`, so a list added
 * later cannot forget to reset — the lint rule on `CursorHistory` state says
 * so. The page size is the one reset a screen still owns, since the screen owns
 * the size.
 *
 * `resetOn` is for what only the screen knows decides its rows: a status tab, a
 * search box that is not the toolbar's.
 *
 * The reset happens while rendering, as `ProductList` resets its selection,
 * rather than in an effect that would paint — and fetch — the stale page first.
 * The stale trail is never returned, not even for the render that notices.
 */
export function useCursorHistory(
  ...resetOn: readonly unknown[]
): [CursorHistory, Dispatch<SetStateAction<CursorHistory>>] {
  const resultSet = useContext(ResultSetContext);
  const key = JSON.stringify([resultSet, resetOn]);

  const [history, setHistory] = useState(firstPage);
  const [seen, setSeen] = useState(key);

  if (seen !== key) {
    const reset = firstPage();
    setSeen(key);
    setHistory(reset);
    return [reset, setHistory];
  }
  return [history, setHistory];
}
